import { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Home, Settings, History, LogOut, Trophy, ChevronLeft, ChevronRight, ArrowLeft } from 'lucide-react';
import axios from '../../../axios-config';
import { useAuth } from '../../contexts/AuthContext';
import { useApp } from '../../contexts/AppContext';
import { houseKioskRoutes } from '../../routes';
import QuickPointsCard from './QuickPointsCard';
import HouseConfigPanel from './HouseConfigPanel';
import TransactionHistory from './TransactionHistory';
import HouseCountSelector from './HouseCountSelector';

type Tab = 'dashboard' | 'configure' | 'history';

export default function TeacherDashboard() {
  const [activeTab, setActiveTab] = useState<Tab>('dashboard');
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const { logout } = useAuth();
  const { activeHouses, houses, getHouse, forceRefresh } = useApp();
  const navigate = useNavigate();
  const location = useLocation();
  const [saveStatus, setSaveStatus] = useState<Record<string, 'idle' | 'saving' | 'saved' | 'error'>>({});

  // Force refresh standings when navigating to this page
  useEffect(() => {
    void forceRefresh();
  }, [forceRefresh, location.key]);

  const handleLogout = () => {
    logout();
    navigate(houseKioskRoutes.login);
  };

  const handleBack = () => {
    // Simple back navigation; fallback to login
    try {
      navigate('/');
    } catch { 
      navigate(houseKioskRoutes.login);
    }
  };

  const saveHouseToOrganization = async (houseId: string) => {
    const current = getHouse(houseId) || houses.find(h => h.id === houseId) || activeHouses.find(h => h.id === houseId);
    if (!current) return;
    setSaveStatus(prev => ({ ...prev, [houseId]: 'saving' }));

    // Fetch full organization first to avoid partial payload errors
    let existingOrg: any | null = null;
    try {
      const { data } = await axios.get(`/api/organizations/${houseId}`, { withCredentials: true });
      existingOrg = Array.isArray(data) ? (data.find((o: any) => o.organizationId === houseId) ?? null) : (data?.organization ?? data ?? null);
    } catch {
      try {
        const { data } = await axios.get('/api/organizations', { withCredentials: true });
        const list = Array.isArray(data) ? data : (data?.organizations || []);
        existingOrg = list.find((o: any) => o.organizationId === houseId) ?? null;
      } catch (err) {
        console.error('Unable to fetch existing organization before save:', err);
      }
    }

    // Build a full payload by merging existing fields with the updated house values
    const safeSlug = (name: string) => name.toLowerCase().trim().replace(/[^a-z0-9]+/g, '-');
    const payload: any = {
      ...(existingOrg || {}),
      organizationId: houseId,
      organizationName: current.name,
      organizationTypeId: existingOrg?.organizationTypeId ?? 9,
      organizationSlug: existingOrg?.organizationSlug ?? safeSlug(current.name),
      mascotName: existingOrg?.mascotName ?? (existingOrg?.mascotName || ''),
      location: existingOrg?.location ?? (existingOrg?.location || ''),
      pointsAvailable: existingOrg?.pointsAvailable ?? (existingOrg?.pointsAvailable || 0),
      // Keep typical audit fields if present
      createdAt: existingOrg?.createdAt ?? new Date().toISOString(),
      createdBy: existingOrg?.createdBy ?? null,
      updatedAt: new Date().toISOString(),
      updatedBy: existingOrg?.updatedBy ?? null,
      deactivatedOn: current.deactivatedOn ?? null,
      // Visual + points fields from current house
      icon: current.icon,
      color: current.color,
      accent: current.accent,
      gemGradient: current.gemGradient,
      backgroundImageUrl: current.backgroundImage || '',
      // Prefer pointsAwarded for houses; preserve if server expects it
      pointsAwarded: current.points,
      // Collections preserved
      acorns: existingOrg?.acorns ?? [],
      organizationType: existingOrg?.organizationType ?? null,
      organizationUsers: existingOrg?.organizationUsers ?? [],
      stores: existingOrg?.stores ?? [],
    };

    try {
      await axios.put(`/api/organizations/${houseId}`, payload, { withCredentials: true });
      setSaveStatus(prev => ({ ...prev, [houseId]: 'saved' }));
    } catch (error) {
      console.error('Error saving organization:', error);
      setSaveStatus(prev => ({ ...prev, [houseId]: 'error' }));
    }
  };

  const tabs = [
    { id: 'dashboard' as Tab, label: 'Dashboard', icon: Home },
    { id: 'configure' as Tab, label: 'Configure Houses', icon: Settings },
    { id: 'history' as Tab, label: 'Transaction History', icon: History },
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900 flex">
      {/* Sidebar */}
      <div
        className={`${
          sidebarCollapsed ? 'w-20' : 'w-64'
        } bg-black/30 backdrop-blur-xl border-r border-white/10 flex flex-col transition-all duration-300`}
      >
        <div className="p-4 border-b border-white/10">
          <div className="flex items-center justify-between">
            {!sidebarCollapsed && (
              <h1 className="text-xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-yellow-400 to-amber-500">
                TEACHER
              </h1>
            )}
            <div className="flex items-center gap-2">
              <button
                onClick={handleBack}
                className="p-2 rounded-lg bg-white/5 hover:bg-white/10 text-white/60 hover:text-white transition-colors"
                aria-label="Back"
              >
                <ArrowLeft size={20} />
              </button>
            <button
              onClick={() => setSidebarCollapsed(!sidebarCollapsed)}
              className="p-2 rounded-lg bg-white/5 hover:bg-white/10 text-white/60 hover:text-white transition-colors"
            >
              {sidebarCollapsed ? <ChevronRight size={20} /> : <ChevronLeft size={20} />}
            </button>
            </div>
          </div>
        </div>

        <nav className="flex-1 p-4 space-y-2">
          {tabs.map(tab => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`w-full flex items-center ${
                sidebarCollapsed ? 'justify-center' : 'space-x-3'
              } p-3 rounded-xl transition-all duration-300 ${
                activeTab === tab.id
                  ? 'bg-gradient-to-r from-yellow-500/20 to-amber-500/20 text-yellow-400 border border-yellow-500/30'
                  : 'text-white/60 hover:bg-white/5 hover:text-white'
              }`}
            >
              <tab.icon size={20} />
              {!sidebarCollapsed && <span className="font-medium">{tab.label}</span>}
            </button>
          ))}

          <button
            onClick={() => navigate(houseKioskRoutes.leaderboard)}
            className={`w-full flex items-center ${
              sidebarCollapsed ? 'justify-center' : 'space-x-3'
            } p-3 rounded-xl text-white/60 hover:bg-white/5 hover:text-white transition-all duration-300`}
          >
            <Trophy size={20} />
            {!sidebarCollapsed && <span className="font-medium">View Standings</span>}
          </button>
        </nav>

        <div className="p-4 border-t border-white/10">
          <button
            onClick={handleLogout}
            className={`w-full flex items-center ${
              sidebarCollapsed ? 'justify-center' : 'space-x-3'
            } p-3 rounded-xl text-red-400 hover:bg-red-500/10 transition-all duration-300`}
          >
            <LogOut size={20} />
            {!sidebarCollapsed && <span className="font-medium">Logout</span>}
          </button>
        </div>
      </div>

      {/* Main Content */}
      <div className="flex-1 overflow-auto">
        <div className="p-8">
          {activeTab === 'dashboard' && (
            <div className="space-y-8">
              <div>
                <h2 className="text-3xl font-bold text-white mb-2">Quick Points</h2>
                <p className="text-white/60">Add or subtract points from any house</p>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
                {activeHouses.filter(w => w.deactivatedOn == null).map(house => (
                  <QuickPointsCard key={house.id} house={house} />
                ))}
              </div>

              <div className="mt-8">
                <HouseCountSelector />
              </div>
            </div>
          )}

          {activeTab === 'configure' && (
            <div className="space-y-8">
              <div>
                <h2 className="text-3xl font-bold text-white mb-2">Configure Houses</h2>
                <p className="text-white/60">Customize house names, icons, and colors</p>
              </div>

              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                {houses.map(house => (
                  <div key={house.id} className="space-y-3">
                    <HouseConfigPanel house={house} />
                    <div className="flex justify-end">
                      <button
                        onClick={() => saveHouseToOrganization(house.id)}
                        className="px-4 py-2 rounded-lg bg-yellow-500 hover:bg-yellow-600 text-black font-semibold transition-colors"
                      >
                        {saveStatus[house.id] === 'saving' && 'Saving...'}
                        {saveStatus[house.id] === 'saved' && 'Saved'}
                        {saveStatus[house.id] === 'error' && 'Retry Save'}
                        {(!saveStatus[house.id] || saveStatus[house.id] === 'idle') && 'Save Changes'}
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {activeTab === 'history' && (
            <div className="space-y-8">
              <div>
                <h2 className="text-3xl font-bold text-white mb-2">Transaction History</h2>
                <p className="text-white/60">View all point changes</p>
              </div>

              <TransactionHistory />
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
